import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { toast } from 'sonner'
import { confirmDialog } from '@/lib/confirmDialog'
import CalendarUpload from '@/components/scrimmage/CalendarUpload'

/**
 * TeamCalendarSettings Component
 * 
 * Manage team calendar for calendar-aware scrimmage matching
 * 
 * Features:
 * - Upload calendar (URL or file)
 * - View calendar info
 * - Re-sync calendar
 * - Delete calendar
 * - View last sync time
 */

export default function TeamCalendarSettings({ teamId, team }) {
  const [calendarInfo, setCalendarInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showUpload, setShowUpload] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [eventCount, setEventCount] = useState(null)

  useEffect(() => {
    loadCalendarInfo()
  }, [teamId])

  useEffect(() => {
    if (calendarInfo) {
      const loadEventCount = async () => {
        const { count } = await supabase
          .from('calendar_events')
          .select('*', { count: 'exact', head: true })
          .eq('team_id', teamId)
        setEventCount(count)
      }
      loadEventCount()
    }
  }, [teamId, calendarInfo])

  const loadCalendarInfo = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('team_calendars')
        .select('*')
        .eq('team_id', teamId)
        .single()

      if (error && error.code !== 'PGRST116') {
        // PGRST116 = not found, which is fine
        console.error('[TeamCalendarSettings] Error loading calendar:', error)
      }

      setCalendarInfo(data)
    } catch (error) {
      console.error('[TeamCalendarSettings] Error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCalendarUploaded = (result) => {
    setShowUpload(false)
    loadCalendarInfo()
    toast.success('Calendar uploaded successfully!')
  }

  const handleResync = async () => {
    if (!calendarInfo || !calendarInfo.calendar_url) {
      toast.error('No calendar URL to sync')
      return
    }

    setSyncing(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-team-calendar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          teamId,
          calendarUrl: calendarInfo.calendar_url,
          calendarType: 'webcal',
          calendarName: calendarInfo.calendar_name
        })
      })

      const data = await response.json()

      if (!response.ok) {
        toast.error(data.error || 'Failed to sync calendar')
        return
      }

      toast.success(`Calendar synced! ${data.eventsCount} events found.`)
      loadCalendarInfo()
    } catch (error) {
      console.error('[TeamCalendarSettings] Error syncing:', error)
      toast.error('Failed to sync calendar')
    } finally {
      setSyncing(false)
    }
  }

  const handleDelete = async () => {
    const confirmed = await confirmDialog(
      'Delete team calendar?',
      {
        description: 'This will remove your calendar and all imported events. You can always upload it again later.',
        confirmText: 'Delete',
        cancelText: 'Cancel'
      }
    )

    if (!confirmed) return

    try {
      // Delete calendar events
      await supabase
        .from('calendar_events')
        .delete()
        .eq('team_id', teamId)

      // Delete calendar metadata
      await supabase
        .from('team_calendars')
        .delete()
        .eq('team_id', teamId)

      // Update team verified flag
      await supabase
        .from('teams')
        .update({ calendar_verified: false })
        .eq('id', teamId)

      toast.success('Calendar deleted')
      setCalendarInfo(null)
    } catch (error) {
      console.error('[TeamCalendarSettings] Error deleting:', error)
      toast.error('Failed to delete calendar')
    }
  }

  if (loading) {
    return (
      <Card>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-green mx-auto"></div>
          <p className="text-sm text-gray-500 mt-2">Loading calendar...</p>
        </div>
      </Card>
    )
  }

  if (showUpload || !calendarInfo) {
    return (
      <Card>
        <h2 className="font-heading text-xl font-bold mb-4">
          Team Calendar
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          Upload your team calendar to enable calendar-aware scrimmage matching and get automatic alerts for compatible scrimmages.
        </p>
        <CalendarUpload
          mode="settings"
          teamId={teamId}
          onCalendarUploaded={handleCalendarUploaded}
          onCancel={calendarInfo ? () => setShowUpload(false) : null}
        />
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="font-heading text-xl font-bold mb-2">Team Calendar</h2>
            <p className="text-sm text-gray-600">
              Your team calendar is synced and ready for scrimmage matching
            </p>
          </div>
          {team.calendar_verified && (
            <Badge variant="success">✓ Verified</Badge>
          )}
        </div>

        {/* Calendar Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Calendar Name</p>
              <p className="font-medium text-brand-charcoal">{calendarInfo.calendar_name || 'Team Calendar'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Calendar Type</p>
              <p className="font-medium text-brand-charcoal">
                {calendarInfo.calendar_type === 'webcal' ? 'Webcal URL (Auto-sync)' : 
                 calendarInfo.calendar_type === 'ics_upload' ? 'ICS File Upload' : 'Manual Entry'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Events Imported</p>
              <p className="font-medium text-brand-charcoal">{eventCount !== null ? eventCount : '...'} events</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Last Synced</p>
              <p className="font-medium text-brand-charcoal">
                {calendarInfo.last_synced_at 
                  ? new Date(calendarInfo.last_synced_at).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })
                  : 'Never'}
              </p>
            </div>
          </div>

          {calendarInfo.calendar_url && (
            <div className="mt-4">
              <p className="text-xs text-gray-500 mb-1">Calendar URL</p>
              <p className="text-xs text-gray-600 font-mono break-all bg-white rounded px-2 py-1">
                {calendarInfo.calendar_url}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          {calendarInfo.calendar_url && calendarInfo.sync_enabled && (
            <Button
              variant="primary"
              onClick={handleResync}
              disabled={syncing}
              loading={syncing}
            >
              🔄 Sync Now
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setShowUpload(true)}
          >
            📅 Upload New Calendar
          </Button>
          <Button
            variant="outline"
            onClick={handleDelete}
            className="text-red-600 hover:bg-red-50"
          >
            Delete Calendar
          </Button>
        </div>

        {/* Auto-sync Note */}
        {calendarInfo.calendar_type === 'webcal' && calendarInfo.sync_enabled && (
          <p className="text-xs text-gray-500 mt-4">
            ℹ️ Your calendar is automatically synced twice daily. You can manually sync anytime using the button above.
          </p>
        )}
      </Card>

      {/* How It Works */}
      <Card>
        <h3 className="font-semibold text-brand-charcoal mb-3">How Calendar Matching Works</h3>
        <div className="space-y-2 text-sm text-gray-700">
          <p>• <strong>Automatic Filtering:</strong> When browsing scrimmages, enable "Match My Calendar" to see only scrimmages that work with your schedule</p>
          <p>• <strong>Conflict Detection:</strong> Choose strict mode (free days only), flexible mode (no time overlap), or show all with warnings</p>
          <p>• <strong>Email Alerts:</strong> Save searches and get notified when new scrimmages match your criteria and calendar</p>
          <p>• <strong>Mutual Matching:</strong> Get notified when another team has availability that perfectly aligns with yours</p>
        </div>
      </Card>
    </div>
  )
}

